import axios from 'axios'
import { toast } from 'react-toastify'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { hideLoading, showLoading } from 'react-redux-loading-bar'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import EnrollmentsBillingTable from './components/EnrollmentsBillingTable'
import EnrollmentDetailDrawer from './components/EnrollmentDetailDrawer'
import EnrollmentsFilterBar, { DEFAULT_FILTERS } from './components/EnrollmentsFilterBar'
import EnrollmentsEmptyState from './components/EnrollmentsEmptyState'
import ExportMembersModal from '../members/components/ExportMembersModal'
import MembersSearchInput from '../members/components/MembersSearchInput'
import Loader from '../common/loader/Loader'

import { ReactComponent as DownloadIcon } from '../../assets/images/common/icons/download-icon.svg'

import classes from './EnrollmentsBillingPage.module.scss'
import colors from '../../assets/styles/globals.scss'

import useReportPolling from '../dashboard/coachDashboard/useReportPolling'
import { deserialize, serialize } from '../../utility/serialization'
import { debounce } from '../../utility/debounceHelper'
import { requestGetEnrollments, requestExportEnrollments } from '../../actions/enrollmentsBillingActions'
import useEnrollmentRowActions from './hooks/useEnrollmentRowActions'
import { PER_PAGE_DEFAULT } from '../../misc/Constants'
import { DEFAULT_SORT_OPTION } from '../../utility/enrollmentsBilling'
import Axios from '../../axios'
import EnrollmentsBillingContext from './EnrollmentsBillingContext'


const EnrollmentsBillingPage = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const { registerRefresh } = useContext(EnrollmentsBillingContext)

  const [enrollments, setEnrollments] = useState([])
  const [pagination, setPagination] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [isLoading, setIsLoading] = useState(false)
  const [isSortLoading, setIsSortLoading] = useState(false)
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [programOptions, setProgramOptions] = useState([])
  const [packageOptions, setPackageOptions] = useState([])

  const [exportId, setExportId] = useState(null)
  const [exportUrl, exportError, clearReportPolling] = useReportPolling({ exportId, setExportId })
  const isExporting = !!(exportId)

  // Load programs for filter dropdown
  useEffect(() => {
    Axios.get('/api/v1/courses', { params: serialize({ communityId }) })
      .then(response => {
        const data = deserialize(response.data)
        const courses = data.courses || []
        setProgramOptions(courses.map(c => ({ label: c.name, value: c.id })))
      })
      .catch(() => { /* ignore - filters will just not have program options */ })
  }, [communityId])

  // Load packages for filter dropdown
  useEffect(() => {
    Axios.get('/api/v1/packages')
      .then(response => {
        const data = deserialize(response.data)
        const packages = data.packages || data || []
        const list = Array.isArray(packages) ? packages : []
        setPackageOptions(list.map(p => ({ label: p.name, value: p.id })))
      })
      .catch(() => { /* ignore - filters will just not have package options */ })
  }, [])

  const fetchEnrollments = useCallback(
    ({ page, search, sortField, sortValue, currentFilters, append }) => {
      abortController.current?.abort()
      abortController.current = new AbortController()

      const filterParams = buildFilterParams(currentFilters)

      return requestGetEnrollments({
        communityId,
        page,
        perPage: PER_PAGE_DEFAULT,
        search,
        sortField,
        sortValue,
        filters: filterParams,
        signal: abortController.current?.signal,
      })
        .then(response => {
          const data = deserialize(response.data)
          setEnrollments(prev => {
            if (append) { return [...prev, ...data.enrollments] }
            return data.enrollments
          })
          setPagination(data.meta.pagination)
        })
    },
    [communityId]
  )

  const updateEnrollments = useCallback(
    (params) => {
      setIsLoading(true)
      fetchEnrollments({
        page: 1,
        search: searchText,
        append: false,
        sortField: sortOption.sortField,
        sortValue: sortOption.sortValue,
        currentFilters: filters,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error(t('enrollmentsBilling.page.genericError'), { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => { setIsLoading(false); setIsSortLoading(false) })
    },
    [fetchEnrollments, searchText, sortOption.sortField, sortOption.sortValue, filters, t]
  )

  // Initial load
  useEffect(() => {
    updateEnrollments({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Expose the table refresh to the layout header's Create Enrollment action (see EnrollmentsBillingContext).
  useEffect(() => {
    registerRefresh(() => updateEnrollments({}))
  }, [registerRefresh, updateEnrollments])

  const fetchMoreEnrollments = () => {
    updateEnrollments({ page: pagination?.page + 1, append: true })
  }

  const debouncedSearch = useMemo(
    () => debounce((search) => updateEnrollments({ search })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleSorting = useCallback((sortField, sortValue) => {
    setIsSortLoading(true)
    setSortOption({ sortField, sortValue })
    updateEnrollments({ sortField, sortValue })
  }, [updateEnrollments])

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    updateEnrollments({ currentFilters: newFilters })
  }, [updateEnrollments])

  const handleRowClick = (enrollment) => {
    setSelectedEnrollment(enrollment)
  }

  const handleClearAll = () => {
    setSearchText('')
    setFilters(DEFAULT_FILTERS)
    updateEnrollments({ search: '', currentFilters: DEFAULT_FILTERS })
  }

  const handleLearnMoreMarketing = () => {
    window.open('https://intercom.help/op-36/en/articles/11144149-onboarding-101-announce-programs', '_blank', 'noopener,noreferrer')
  }

  const { openApproveModal, openDeclineModal, modals: rowActionModals } =
    useEnrollmentRowActions({ onActionComplete: () => updateEnrollments({}) })

  const handleExport = () => {
    dispatch(showLoading())

    const filterParams = buildFilterParams(filters)

    requestExportEnrollments({ communityId, filters: { ...filterParams, search: searchText } })
      .then((response) => {
        setExportId(deserialize(response.data).jobId)
      })
      .catch(() => toast.error(t('enrollmentsBilling.page.genericError'), { position: toast.POSITION.TOP_RIGHT }))
  }

  const clearExportResult = () => {
    dispatch(hideLoading())
    clearReportPolling()
  }

  useEffect(() => {
    if (exportUrl) {
      dispatch(hideLoading())
    }
  }, [exportUrl, exportError, dispatch])

  if (enrollments === null && isLoading) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>{t('enrollmentsBilling.page.loading')}</Loader>
      </div>
    )
  }

  return (
    <main className={classes.wrapper}>
      <header className={classes.toolbar}>
        <div className={classes.leftGroup}>
          <div className={classes.searchWrapper}>
            <MembersSearchInput placeholder={t('enrollmentsBilling.page.searchPlaceholder')} value={searchText} onChange={handleSearch} />
          </div>
          <EnrollmentsFilterBar
            appliedFilters={filters}
            onApplyFilters={handleApplyFilters}
            programOptions={programOptions}
            packageOptions={packageOptions}
          />
        </div>
        <button
          id='exportEnrollmentsButton'
          onClick={handleExport}
          className={classes.exportButton}
          disabled={isExporting}
          aria-label={t('enrollmentsBilling.page.exportAriaLabel')}
        >
          {t('enrollmentsBilling.page.export')} <DownloadIcon color={colors.darkNavy} width={18} height={18} />
        </button>
      </header>

      {enrollments?.length === 0 && !isLoading ? (
        <EnrollmentsEmptyState
          hasFilters={hasActiveFilters(filters)}
          hasSearch={!!searchText}
          onClearAll={handleClearAll}
          onLearnMore={handleLearnMoreMarketing}
        />
      ) : (
        <EnrollmentsBillingTable
          enrollments={enrollments}
          fetchData={fetchMoreEnrollments}
          onRowClick={handleRowClick}
          onApprove={openApproveModal}
          onDecline={openDeclineModal}
          pagination={pagination}
          selectedEnrollmentId={selectedEnrollment?.id}
          isExporting={isExporting}
          isLoading={isLoading}
          isSortLoading={isSortLoading}
          sortOption={sortOption}
          applySort={handleSorting}
        />
      )}

      {(exportUrl || exportError) && (
        <ExportMembersModal exportUrl={exportUrl} exportError={exportError} clearExportResult={clearExportResult} />
      )}

      {!!selectedEnrollment && (
        <EnrollmentDetailDrawer
          enrollment={selectedEnrollment}
          communityId={communityId}
          onClose={() => setSelectedEnrollment(null)}
          onActionComplete={() => updateEnrollments({})}
        />
      )}

      {rowActionModals}
    </main>
  )
}

const buildFilterParams = (filters) => {
  const params = {}
  if (filters?.enrollmentStatus?.length) {
    params.enrollmentStatus = filters.enrollmentStatus
  }
  if (filters?.invoiceStatus?.length) {
    params.invoiceStatus = filters.invoiceStatus
  }
  if (filters?.courseId) {
    params.courseId = filters.courseId
  }
  if (filters?.packageId) {
    params.packageId = filters.packageId
  }
  if (filters?.dateJoinedStart) {
    params.dateJoinedStart = filters.dateJoinedStart
  }
  if (filters?.dateJoinedEnd) {
    params.dateJoinedEnd = filters.dateJoinedEnd
  }
  return params
}

const hasActiveFilters = (filters) => (
  (filters.enrollmentStatus?.length > 0) ||
  (filters.invoiceStatus?.length > 0) ||
  !!filters.courseId ||
  !!filters.packageId ||
  !!filters.dateJoinedStart ||
  !!filters.dateJoinedEnd
)

export default EnrollmentsBillingPage
